import { useFrame } from '@react-three/fiber';
import { useRef, useState } from 'react';
import * as THREE from 'three';

// Reused per-frame to avoid GC pressure inside `useFrame`.
const _tmpWorld = new THREE.Vector3();

/**
 * Sphere-plus-3D-cross vertex marker. Used by every creation tool (Add
 * Point, Line, Polygon) so picked vertices, hover previews and snap
 * targets have a single coherent look.
 *
 * Behaviours:
 *   • **Screen-space sizing** — the marker auto-scales with camera
 *     distance so it stays roughly the same visual size at any zoom. Tune
 *     via `screenSize` (target world size at unit camera distance).
 *   • **Hover state** — when `onPointerDown` is supplied (i.e. the marker
 *     is draggable) the marker grows + brightens on hover and the cursor
 *     becomes `grab`. `cursor` falls back to `pointer` if you want
 *     interactivity without dragging.
 *   • **Duck-typed position** — `'x' in position` rather than
 *     `instanceof THREE.Vector3` so multiple bundled three.js copies
 *     (drei, pnext, etc.) don't break it.
 *   • **`depthTest=false`** keeps it visible against any surface.
 */
export const CrosshairMarker = ({
  position,
  color,
  opacity = 0.95,
  sphereOpacity,
  /** Marker size as a fraction of the canvas height — `0.014` on a 1080 px
   *  viewport reads as a ~15 px diameter sphere. Smaller for compact picks
   *  (~0.012 for current vertices), larger for emphasis (~0.022 for
   *  snap-to-first highlight). */
  screenSize = 0.014,
  onPointerDown,
  cursor,
}: {
  position: THREE.Vector3 | [number, number, number] | { x: number; y: number; z: number };
  color: string;
  opacity?: number;
  sphereOpacity?: number;
  screenSize?: number;
  onPointerDown?: (e: any) => void;
  /** Override the cursor while hovering the marker (defaults to `grab`
   *  when `onPointerDown` is set, no change otherwise). */
  cursor?: string;
}) => {
  let pos: [number, number, number];
  if (Array.isArray(position)) {
    pos = [position[0], position[1], position[2]];
  } else if (position && typeof position === 'object' && 'x' in position) {
    pos = [(position as any).x, (position as any).y, (position as any).z];
  } else {
    return null;
  }

  const groupRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);
  const sphereO = sphereOpacity ?? Math.min(opacity * 0.6, 0.6);
  const interactive = !!onPointerDown;
  const hoverCursor = cursor ?? (interactive ? 'grab' : null);

  // Per-frame screen-space scaling. Mirrors MeasuringTool / AreaMeasuringTool
  // — for both perspective and orthographic cameras, derive `unitsPerPixel`
  // from the actual visible viewport, then size the marker as
  // `targetPixels * unitsPerPixel`.
  //
  // Why this matters for ortho: `camera.position.distanceTo(world)` in
  // ortho mode reads the artificial dolly distance (Viewer.tsx dollies
  // the ortho camera to 1000 m for top-down view), not the actual
  // on-screen scale. Using that distance produced markers ~10× too big
  // in ortho. The right metric is `(top - bottom) / zoom / canvasHeight`,
  // which gives metres-per-pixel in the current view.
  //
  // We MUST read the marker's WORLD position for the perspective branch
  // (not `g.position`) — once the marker lives inside a floating-origin
  // parent group the local coords are tiny while the world is at UTM
  // scale.
  useFrame(({ camera, size }) => {
    const g = groupRef.current;
    if (!g) return;
    const targetPixels = Math.max(screenSize * size.height, 4);
    let unitsPerPixel: number;
    if (camera.type === 'OrthographicCamera') {
      const o = camera as THREE.OrthographicCamera;
      const worldScreenHeight = Math.abs(o.top - o.bottom) / Math.max(o.zoom, 1e-6);
      unitsPerPixel = worldScreenHeight / size.height;
    } else {
      g.getWorldPosition(_tmpWorld);
      const dist = camera.position.distanceTo(_tmpWorld);
      const p = camera as THREE.PerspectiveCamera;
      const vFov = (p.getEffectiveFOV?.() ?? p.fov) * (Math.PI / 180);
      const worldScreenHeight = 2 * dist * Math.tan(vFov / 2);
      unitsPerPixel = worldScreenHeight / size.height;
    }
    const base = Math.max(targetPixels * unitsPerPixel, 1e-4);
    const s = hovered && interactive ? base * 1.35 : base;
    g.scale.setScalar(s);
  });

  const handlePointerOver = interactive
    ? (e: any) => {
        e.stopPropagation();
        setHovered(true);
        if (hoverCursor && typeof document !== 'undefined') {
          document.body.style.cursor = hoverCursor;
        }
      }
    : undefined;
  const handlePointerOut = interactive
    ? (e: any) => {
        e.stopPropagation();
        setHovered(false);
        if (hoverCursor && typeof document !== 'undefined') {
          document.body.style.cursor = '';
        }
      }
    : undefined;

  // Marker geometry is built at unit scale and the parent group does the
  // screen-space scaling — keeps the cross thickness, sphere radius and
  // cross length in sync without three useFrame writes.
  return (
    <group
      ref={groupRef}
      position={pos}
      onPointerDown={onPointerDown}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}>
      <mesh>
        <sphereGeometry args={[1, 16, 16]} />
        <meshBasicMaterial color={color} opacity={sphereO} transparent depthTest={false} />
      </mesh>
      <mesh>
        <boxGeometry args={[3, 0.4, 0.4]} />
        <meshBasicMaterial color={color} opacity={opacity} transparent depthTest={false} />
      </mesh>
      <mesh rotation={[0, 0, Math.PI / 2]}>
        <boxGeometry args={[3, 0.4, 0.4]} />
        <meshBasicMaterial color={color} opacity={opacity} transparent depthTest={false} />
      </mesh>
    </group>
  );
};
